import { useTranslation } from '@aimsos/i18n';
import { Button, CartContainer, Skeleton } from '@aimsos/ui';

import { formatClockTime } from '../../utils/formatClockTime.js';
import { SUMMARY_STATE } from '../../utils/summaryState.js';
import HowToApproachCard from '../approach/HowToApproachCard.jsx';
import NextBestActionCard from '../next-best-action/NextBestActionCard.jsx';

import { AlertTriangle, CheckCircle2 } from 'lucide-react';

/**
 * The row of Concierge cards under the profile header: the Next Best Action
 * (CT-5713) and How to approach beside it (CT-5714). It owns the states of the
 * PAIR, picked by `summaryState`:
 *
 *   loading   an inline skeleton the size of the card
 *   error     ONE compact row "Couldn't load suggestions · Retry" for both cards
 *   empty     ONE line "No suggestions right now · checked <time>"
 *   cards     the cards that have content; alone, a card takes the full width;
 *             `collapsed` folds each to one line
 *
 * @param {object} props
 * @param {string} props.state — one of `SUMMARY_STATE`.
 * @param {object[]} props.items — the suggestions.
 * @param {string|null} props.updatedAt
 * @param {string|null} props.checkedAt
 * @param {boolean} props.restricted
 * @param {object|null} props.approach — `useHowToApproach().approach`.
 * @param {boolean} props.collapsed
 * @param {{ type: string, id: string, title: string }} props.entity — the record the Concierge actions are about.
 * @param {() => void} props.onRetry — re-requests both cards.
 * @param {(item: object, action: object) => void} props.onAction
 * @param {(item: object) => void} props.onOpenDetails
 * @param {(source: { kind: string, id: string }) => void} props.onOpenSource
 */
function ProfileSummaryCards({
  state,
  items,
  updatedAt,
  checkedAt,
  restricted,
  approach,
  collapsed,
  entity,
  onRetry,
  onAction,
  onOpenDetails,
  onOpenSource,
}) {
  const { t, i18n } = useTranslation('unified-profiles');

  if (state === SUMMARY_STATE.LOADING) {
    return (
      <div data-testid="summary-loading" aria-busy>
        <Skeleton width="100%" height="148px" borderRadius="12px" />
      </div>
    );
  }

  if (state === SUMMARY_STATE.ERROR) {
    return (
      <div data-testid="summary-error">
        <CartContainer size="S" variant="neutral">
          <div className="flex items-center gap-2 text-sm text-content-secondary">
            <AlertTriangle size={16} aria-hidden className="shrink-0" />
            <span>{t('profile.summary.error')}</span>
            <Button variant="ghost" size="sm" onClick={onRetry}>
              {t('states.error.retry')}
            </Button>
          </div>
        </CartContainer>
      </div>
    );
  }

  if (state === SUMMARY_STATE.EMPTY) {
    return (
      <p data-testid="summary-empty" className="flex items-center gap-2 text-sm text-content-muted">
        <CheckCircle2 size={16} aria-hidden className="shrink-0" />
        {t('profile.summary.empty', { time: formatClockTime(checkedAt, i18n?.language) ?? '—' })}
      </p>
    );
  }

  return (
    // Folded, each card is one line, so they sit side by side sooner (prototype).
    <div
      data-testid="summary-cards"
      data-collapsed={collapsed ? 'true' : 'false'}
      className={collapsed ? 'flex flex-col gap-4 md:flex-row' : 'flex flex-col gap-4 lg:flex-row'}
    >
      {items.length ? (
        <NextBestActionCard
          items={items}
          updatedAt={updatedAt}
          restricted={restricted}
          collapsed={collapsed}
          entity={entity}
          onAction={onAction}
          onOpenDetails={onOpenDetails}
        />
      ) : null}
      {approach?.fields?.length ? (
        <HowToApproachCard
          approach={approach}
          name={entity.title}
          collapsed={collapsed}
          onOpenSource={onOpenSource}
        />
      ) : null}
    </div>
  );
}

export default ProfileSummaryCards;
